/**
 * Zoom + pan for a mounted Rive artboard — the "show me only this character"
 * viewport used by the Character tab (each character's head, framed out of the
 * shared scene artboard) and by the Animation tab.
 *
 * HOW IT WORKS: Rive's `Layout` carries a destination FRAME rect in
 * drawing-surface pixels, which the renderer fits the artboard into
 * (`renderer.align(fit, alignment, frame, artboardBounds)` in rive.js). A frame
 * LARGER than the canvas, offset negatively, therefore renders the artboard
 * zoomed and panned — vector-crisp at any magnification, with no second canvas,
 * no CSS transform and no change to the .riv.
 *
 * There is no runtime API for hiding a group (the JS `Artboard` exposes name,
 * bounds, volume and size — nothing about the scene graph), so framing is the
 * only way to isolate one character from an artboard that holds three.
 *
 * ⚠ Rive RESETS the frame to the plain canvas rect on every resize
 * (`resizeToCanvas`, which @rive-app/react-webgl2 calls from its own
 * ResizeObserver), so every path that resizes the canvas must re-apply the view
 * afterwards. Both callers do that from a resize ladder.
 */

/** Magnification (1 = the whole artboard fits) + the artboard point held centre. */
export interface RiveView {
  zoom: number;
  /** Artboard-space x of the point pinned to the centre of the canvas. */
  cx: number;
  /** Artboard-space y of the point pinned to the centre of the canvas. */
  cy: number;
}

/** A rect of the artboard, in artboard pixels (top-left origin, as in Rive). */
export interface ViewRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Canvas measurements needed to convert between artboard and surface space. */
export interface ViewMetrics {
  /** Drawing-surface size in device pixels (canvas.width / canvas.height). */
  W: number;
  H: number;
  /** Artboard unit → surface px at zoom 1 (the Contain fit scale). */
  fit: number;
  /** CSS px → surface px, read from the canvas rather than assumed. */
  dpr: number;
  /** Artboard size these metrics were built for. */
  refW: number;
  refH: number;
}

/** How far in the zoom controls / wheel may go. */
export const MAX_VIEW_ZOOM = 8;

/** The default view: the whole artboard, centred. */
export const fitAllView = (refW: number, refH: number): RiveView => ({
  zoom: 1,
  cx: refW / 2,
  cy: refH / 2,
});

export function viewMetrics(
  canvas: HTMLCanvasElement | null | undefined,
  refW: number,
  refH: number,
): ViewMetrics | null {
  if (!canvas || !canvas.width || !canvas.height) return null;
  return {
    W: canvas.width,
    H: canvas.height,
    fit: Math.min(canvas.width / refW, canvas.height / refH),
    dpr: canvas.clientWidth ? canvas.width / canvas.clientWidth : 1,
    refW,
    refH,
  };
}

/** Minimal shape of the bits of a Rive instance this module touches. */
interface ViewTarget {
  layout: { copyWith: (params: Record<string, number>) => unknown };
  drawFrame: () => void;
}

/**
 * Push `view` onto the rive instance and repaint.
 *
 * The artboard is Contain-fitted inside the frame, so it lands at
 * `frame.min + zoom × letterbox`. Solving "artboard point (cx, cy) sits at the
 * canvas centre" for the frame origin gives the two lines below. At zoom 1 with
 * the artboard's own centre they produce exactly the plain canvas rect — the
 * unzoomed view is what it was before any of this existed.
 */
export function applyRiveView(
  rive: unknown,
  metrics: ViewMetrics | null,
  view: RiveView,
): void {
  const target = rive as ViewTarget | null;
  if (!target || !metrics) return;
  const { W, H, fit, refW, refH } = metrics;
  const s = fit * view.zoom;
  const letterboxX = (W - refW * fit) / 2;
  const letterboxY = (H - refH * fit) / 2;
  const x = W / 2 - view.cx * s - view.zoom * letterboxX;
  const y = H / 2 - view.cy * s - view.zoom * letterboxY;
  try {
    (target as { layout: unknown }).layout = target.layout.copyWith({
      minX: x,
      minY: y,
      maxX: x + W * view.zoom,
      maxY: y + H * view.zoom,
    });
    target.drawFrame();
  } catch {
    /* instance disposed mid-remount — the next mount re-applies */
  }
}

/**
 * Give the canvas its CSS + drawing-surface size, if @rive-app/react-webgl2
 * hasn't.
 *
 * The hook sizes the canvas from its own container ResizeObserver, and that
 * sometimes never fires — the canvas then sits at its default 300×150 surface
 * with `width: 0px; height: 0px`, i.e. an invisible preview no matter what is
 * drawn into it. Reproduced on the un-modified head editor (2026-07-31): a plain
 * page load left the canvas 0×0 until the window was resized, which is the "blank
 * character editor" that looked random. Returns true if it changed anything.
 */
export function ensureCanvasSized(
  wrapper: HTMLElement | null | undefined,
  canvas: HTMLCanvasElement | null | undefined,
): boolean {
  if (!wrapper || !canvas) return false;
  const w = wrapper.clientWidth;
  const h = wrapper.clientHeight;
  if (!w || !h) return false;
  // Same ratio clamp the hook uses, so we never fight it over a fractional dpr.
  const dpr = Math.min(Math.max(1, window.devicePixelRatio || 1), 3);
  const surfaceW = Math.round(w * dpr);
  const surfaceH = Math.round(h * dpr);
  if (canvas.style.width === `${w}px` && canvas.style.height === `${h}px` && canvas.width === surfaceW && canvas.height === surfaceH) {
    return false;
  }
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  canvas.width = surfaceW;
  canvas.height = surfaceH;
  return true;
}

/**
 * Hand the frame back to Rive: its own layout (the plain canvas rect) IS fit-all.
 *
 * Callers represent fit-all as `null` rather than `{zoom: 1, centre}` — computing
 * a centre needs the artboard's size, which isn't known until it has loaded, and a
 * fit-all view built from a guessed size lands the artboard off-canvas (measured
 * 2026-07-31: a blank preview with the frame offset by 313px). `null` can't go
 * stale.
 */
export function resetRiveView(rive: unknown): void {
  const target = rive as ({ resizeToCanvas?: () => void; drawFrame: () => void }) | null;
  if (!target) return;
  try {
    target.resizeToCanvas?.();
    target.drawFrame();
  } catch {
    /* disposed */
  }
}

/**
 * The view that fills the canvas with `rect`.
 *
 * Never zooms out below 1: a rect wider than the canvas would otherwise shrink
 * the artboard inside its own letterbox, which reads as a bug rather than a frame.
 */
export function fitRectView(rect: ViewRect, metrics: ViewMetrics | null): RiveView {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  if (!metrics) return { zoom: 1, cx, cy };
  const zoom = Math.min(
    metrics.W / (rect.w * metrics.fit),
    metrics.H / (rect.h * metrics.fit),
  );
  return { zoom: Math.min(MAX_VIEW_ZOOM, Math.max(1, zoom)), cx, cy };
}

/**
 * Zoom by `factor`, keeping the artboard point under (surfaceX, surfaceY) — a
 * cursor position in SURFACE px — pinned where it is. Omit the point to zoom
 * about the centre.
 */
export function zoomAtView(
  view: RiveView,
  factor: number,
  metrics: ViewMetrics | null,
  surfaceX?: number,
  surfaceY?: number,
): RiveView {
  const zoom = Math.min(MAX_VIEW_ZOOM, Math.max(1, view.zoom * factor));
  if (!metrics || surfaceX === undefined || surfaceY === undefined) return { ...view, zoom };
  const offX = surfaceX - metrics.W / 2;
  const offY = surfaceY - metrics.H / 2;
  // The artboard point under the cursor now…
  const ax = view.cx + offX / (metrics.fit * view.zoom);
  const ay = view.cy + offY / (metrics.fit * view.zoom);
  // …kept under the cursor after the zoom.
  return {
    zoom,
    cx: ax - offX / (metrics.fit * zoom),
    cy: ay - offY / (metrics.fit * zoom),
  };
}

/** Drag the artboard by a surface-px delta (the pointer's movement × dpr). */
export function panView(
  view: RiveView,
  dxSurface: number,
  dySurface: number,
  metrics: ViewMetrics | null,
): RiveView {
  if (!metrics) return view;
  const s = metrics.fit * view.zoom;
  return { ...view, cx: view.cx - dxSurface / s, cy: view.cy - dySurface / s };
}
